/**
 * @typedef {import("./Props").default} Props
 * @typedef {import("../../../App/shared/ContactService/AiMonitoringPreviewResponse").default} AiMonitoringPreviewResponse
 * @typedef {{
 * 	errorMessage: string;
 * 	isLoading: boolean;
 * 	preview: AiMonitoringPreviewResponse | null;
 * }} State
 */

import ActionMessage from '../../../shared/ActionMessages/ActionMessage';
import captureError from '../../../utils/captureError';
import formatCentsForDisplay from '../../../utils/formatCentsForDisplay';
import React from 'react';

/** @extends {React.Component<Props, State>} */
export default class ContactAiMonitoringPopup extends React.Component {
	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			errorMessage: '',
			isLoading: props.mode === 'add',
			preview: null,
		};
	}

	async componentDidMount() {
		if (this.props.mode !== 'add' || !this.props.onPreview) {
			this.setState({ isLoading: false });
			return;
		}

		try {
			const preview = await this.props.onPreview();
			this.setState({
				isLoading: false,
				preview,
			});
		} catch (error) {
			captureError(error);
			const serviceError = /** @type {{message?: string}} */ (error);

			this.setState({
				errorMessage:
					serviceError?.message || 'Unable to load billing preview.',
				isLoading: false,
			});
		}
	}

	#renderMessage() {
		const count = this.props.selectedContacts?.length ?? 0;
		const action =
			this.props.mode === 'add'
				? 'add to Major Life Event Scoring'
				: 'remove from Major Life Event Scoring';

		if (count === 1 && this.props.contactName) {
			return (
				<div>
					<div>You are about to {action} for:</div>
					<span className="font-bold">{this.props.contactName}</span>
				</div>
			);
		}

		return (
			<div>
				You are about to {action} for {count} contact
				{count === 1 ? '' : 's'}.
			</div>
		);
	}

	#renderAddPreview() {
		if (this.state.isLoading) {
			return <p className="mt-4">Loading billing preview...</p>;
		}

		if (this.state.errorMessage) {
			return (
				<p className="mt-4 text-danger">{this.state.errorMessage}</p>
			);
		}

		if (!this.state.preview) {
			return null;
		}

		const preview = this.state.preview;
		const hasChargeChange =
			preview.immediateChargeInCents > 0 ||
			preview.newMonthlyChargeInCents >
				preview.currentMonthlyChargeInCents;

		if (!hasChargeChange) {
			return (
				<div className="mt-4 text-center">
					<p>
						These {preview.contactsToAdd} contact
						{preview.contactsToAdd === 1 ? '' : 's'} will be added
						to Major Life Event Scoring. Your monthly AI Monitoring
						charge will remain{' '}
						{formatCentsForDisplay(preview.newMonthlyChargeInCents)}
						.
					</p>
				</div>
			);
		}

		return (
			<div className="mt-4 text-center">
				<p>
					By adding these {preview.contactsToAdd} contact
					{preview.contactsToAdd === 1 ? '' : 's'} to Major Life Event
					Scoring, you will be charged{' '}
					{formatCentsForDisplay(preview.immediateChargeInCents)}{' '}
					today. Your new monthly charge will be{' '}
					{formatCentsForDisplay(preview.newMonthlyChargeInCents)}.
				</p>
			</div>
		);
	}

	render() {
		const isAdd = this.props.mode === 'add';
		const isAwaitingPreview = isAdd && this.state.isLoading;
		const hasChargeChange =
			(this.state.preview?.immediateChargeInCents ?? 0) > 0 ||
			(this.state.preview?.newMonthlyChargeInCents ?? 0) >
				(this.state.preview?.currentMonthlyChargeInCents ?? 0);
		const canSubmit =
			!isAdd ||
			(!this.state.isLoading &&
				!this.state.errorMessage &&
				this.state.preview !== null);

		return (
			<ActionMessage
				action={
					isAdd
						? isAwaitingPreview || hasChargeChange
							? 'warning'
							: 'theme'
						: 'warning'
				}
				onClose={this.props.onClose}
				onSubmit={canSubmit ? this.props.onSubmit : undefined}
				title={
					isAdd ? 'Add to AI Monitoring' : 'Remove from AI Monitoring'
				}
			>
				{this.#renderMessage()}
				<div>
					{isAdd ? (
						this.#renderAddPreview()
					) : (
						<p className="mt-4">
							Removed contacts will remain in the REMOVED state
							until the current billing cycle ends.
						</p>
					)}
				</div>
			</ActionMessage>
		);
	}
}
